import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { computed, nextTick, onBeforeMount, provide, ref, useSlots, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'Components/primitives/table';
import EnDatagridTopPanel from './slots/EnDatagridTopPanel.vue';
import EnDatagridHeaderList from './slots/EnDatagridHeaderList.vue';
import EnDatagridPagination from './slots/EnDatagridPagination.vue';
import EnDatagridContextMenu from './slots/EnDatagridContextMenu.vue';
import EnExportAllDataDialog from './actions/EnExportAllDataDialog.vue';
import EnCheckbox from '../inputs/EnCheckbox/EnCheckbox.vue';
import EnSnackbar from 'Components/feedback/EnSnackbar/EnSnackbar.vue';
import { EnButton } from '../base/EnButton';
import EnSearchIcon from 'Components/icons/EnSearchIcon.vue';
import { EnSkeleton } from 'Components/base/EnSkeleton';
import EnRadioGroupItem from 'Components/inputs/EnRadioGroup/EnRadioGroupItem.vue';
import { ColumnSortDirections } from './types/columnSortDirection';
import Filter from './types/filter';
import { SnackbarType } from 'Components/types/SnackbarType';
import { FilterType } from './types/filterType';
import VarType from 'Js/enums/varType';
import { LogicalOperator } from 'Js/types/logicalOperator';
import { FilterButtonType } from './types/filterButtonType';
import { RequestMethods } from 'Js/types/fetch/requestMethod';
// Utils
import cloneDeep from 'lodash.clonedeep';
import { camelCaseToKebabCase, isNullOrEmpty } from 'Js/utils/stringUtils';
import { addTermToFilter } from './utils/searchUtils';
import { enFetch } from 'Js/utils/enFetch';
import { useFilterManagement } from './composables/filterComposable';
import { useColumnState } from './composables/useColumnState';
import { useDataGridOptions } from './composables/useDataGridOptions';
import { useItemNavigation } from './composables/useItemNavigation';
import { useCalculateBodyHeight } from './composables/calculateBodyHeight';
import { useStickyCheckboxWidth } from './composables/useStickyCheckboxWidth';
import { useResponseExceptionParser } from './composables/useResponseExceptionParser';
import { useHeadersBuilder, resolveNestedValue } from './composables/useHeadersBuilder';
import { useDataGridSelection } from './composables/useDataGridSelection';
import { useDataGridExpansion } from './composables/useDataGridExpansion';
import { ChevronDown, ChevronUp, LoaderCircle } from 'lucide-vue-next';
import { useIdBuilder } from 'Components/datagrid/composables/useIdBuilder';
export default /*@__PURE__*/ _defineComponent({
    __name: 'EnDataGrid',
    props: /*@__PURE__*/ _mergeModels({
        items: { type: Array, required: false, default: () => [] },
        headers: { type: Array, required: true, default: () => [] },
        lookups: { type: Array, required: false },
        getUri: { type: String, required: false },
        getParameters: { type: null, required: false },
        mapping: { type: Function, required: false },
        customSortMap: { type: Array, required: false },
        postUri: { type: [String, Function], required: false },
        putUri: { type: [String, Function], required: false },
        deleteUri: { type: [String, Function], required: false },
        customPostItem: { type: Function, required: false },
        customPutItem: { type: Function, required: false },
        customDeleteItem: { type: Function, required: false },
        createPostItem: { type: Function, required: false },
        createPutItem: { type: Function, required: false },
        additionalGetParams: { type: Array, required: false, default: () => [] },
        requireTotalCount: { type: Boolean, required: false, default: true },
        defaultSortOrder: { type: Array, required: false, default: () => [] },
        showSelect: { type: Boolean, required: false, default: false },
        showSingleSelect: { type: Boolean, required: false, default: false },
        showExpand: { type: Boolean, required: false, default: false },
        expandOnClick: { type: Boolean, required: false, default: false },
        singleExpand: { type: Boolean, required: false, default: false },
        showExportButton: { type: Boolean, required: false, default: false },
        exportFileName: { type: String, required: false },
        showActionButton: { type: Boolean, required: false, default: true },
        contextMenuActions: { type: Array, required: false },
        additionalActions: { type: Array, required: false },
        batchProcessTitle: { type: null, required: false },
        batchProcessItemCount: { type: null, required: false },
        secondary: { type: Boolean, required: false, default: false },
        dense: { type: Boolean, required: false, default: false },
        isFixedHeader: { type: Boolean, required: false, default: true },
        maxBodyHeight: { type: Number, required: false },
        actionColumnWidth: { type: [String, Number], required: false },
        hideFooter: { type: Boolean, required: false, default: false },
        hideTopPanel: { type: Boolean, required: false, default: false },
        preserveColumnWidth: { type: Boolean, required: false, default: false },
        selectedItems: { type: Array, required: false },
        itemKey: { type: String, required: false, default: 'Id' },
        itemSelectable: { type: [String, Function], required: false },
        showFilterBar: { type: Boolean, required: false, default: false },
        showFilterOptions: { type: Boolean, required: false, default: true },
        showResetFilterButton: { type: Boolean, required: false, default: false },
        shortcutFilterButtons: { type: Array, required: false, default: () => [] },
        saveStateInUrl: { type: Boolean, required: false, default: true },
        additionalHiddenFilters: { type: Object, required: false },
        editDialogOptions: { type: Object, required: false },
        defaultItem: { type: Function, required: false, skipCheck: true },
        fixedActions: { type: Boolean, required: false },
        allowEditing: { type: Boolean, required: false },
        allowAdding: { type: Boolean, required: false },
        allowDeleting: { type: Boolean, required: false },
        allowCopying: { type: Boolean, required: false },
        editPrevention: { type: Function, required: false },
        deletePrevention: { type: Function, required: false },
        deletePreventionTooltip: { type: String, required: false },
        editFormIsInvalid: { type: Boolean, required: false },
        editDialogTitle: { type: String, required: false },
        addDialogTitle: { type: String, required: false },
        staticId: { type: String, required: false },
        options: { type: null, required: false, default: {} }
    }, {
        "modelValue": {},
        "modelModifiers": {},
    }),
    emits: /*@__PURE__*/ _mergeModels([
        'update:selectedItems',
        'update:items',
        'update:isLoading',
        'update:totalItems',
        'update:modelValue',
        'update:headers',
        'click:row',
        'item-deleted',
        'item-added',
        'item-updated',
        'row-clicked',
        'edit-dialog-closed',
        'edited-dialog-opened',
        'delete-dialog-closed',
        'details-dialog-close',
        'update:options',
        'column-visibility-toggle-all',
        'column-visibility-toggle',
        'export-all-data',
        'reset-individual-filter',
        'filters-changed',
    ], ["update:modelValue"]),
    setup(__props, { expose: __expose, emit: __emit }) {
        const { t } = useI18n();
        const slots = useSlots();
        const { parseResponseException } = useResponseExceptionParser();
        const props = __props;
        const emit = __emit;
        // Refs
        const modelValue = _useModel(__props, 'modelValue');
        const snackbar = ref();
        const top = ref();
        const pagination = ref();
        const tableWrapper = ref();
        const { stickyCheckboxWidth } = useStickyCheckboxWidth(tableWrapper);
        // State
        const internalItems = ref([]);
        const dataGridKey = ref(0);
        const internalTotalItems = ref(0);
        const lookupItems = {};
        const showExportDialog = ref(false);
        const loading = ref(false);
        let loadController = null;
        const contextMenuItem = ref(null);
        const snackbarType = ref(SnackbarType.INFO);
        const currentItem = ref(modelValue.value);
        const hoveredColumnKey = ref(null);
        const batchProcessItemCount = computed(() => props.batchProcessItemCount);
        const batchProcessTitle = computed(() => props.batchProcessTitle);
        // CRUD State
        const showEditDialog = ref(false);
        const showDeleteDialog = ref(false);
        const editDialogErrorMessage = ref();
        const isEditing = ref(false);
        const isAdding = ref(false);
        const postUrl = props.postUri ?? props.getUri;
        const putUrl = props.putUri ?? props.getUri;
        const deleteUrl = props.deleteUri ?? props.getUri;
        // Filtering
        const filterExpr = ref([]);
        const noLoading = ref(false);
        const filterButtons = ref([]);
        const filters = ref([]);
        const router = useRouter();
        const route = useRoute();
        const { options, calculateSkeletonWidth } = useDataGridOptions(props);
        const currentHeaders = ref(props.headers);
        const { columnState, isDefaultView, applyColumnOrder, loadColumnState, reloadColumnState, saveColumnState, restoreDefaultColumnState, toUrlParams: columnStateUrlParams, } = useColumnState({
            headers: currentHeaders,
            getSortBy: () => options.value.sortBy,
            setSortBy: (sortBy) => options.value.sortBy = sortBy,
            staticId: props.staticId,
            moduleName: typeof route?.name === VarType.String ? route.name : undefined,
            saveStateInUrl: props.saveStateInUrl,
        });
        let { updateFilters, resetFilters, createFilterExpression, loadFiltersAndUpdate } = useFilterManagement(filters, filterExpr, noLoading, { toUrlParams: columnStateUrlParams }, filtersChanged, router, route, props.saveStateInUrl);
        const { buildIdForItem } = useIdBuilder();
        // The options and filters the shown rows were loaded with, to fall back on when a load is cancelled.
        let appliedOptions = cloneDeep(options.value);
        let appliedFilters = null;
        // Putting the filters back changes the filter expression, which must not start yet another load.
        let restoringAppliedState = false;
        // Computed
        const displayItems = computed(() => props.getUri ? internalItems.value : props.items);
        const hasFiltersSet = computed(() => Object.values(filters.value)?.filter((f) => f.value !== f.resetValue).length > 0);
        const { bodyHeight } = useCalculateBodyHeight(tableWrapper, props, top, pagination, options, filterButtons, displayItems);
        const displayTotalItems = computed(() => props.getUri ? internalTotalItems.value : (props.items?.length ?? 0));
        const detailsNavigation = useItemNavigation(displayItems, emit);
        const hasDetailsDialog = computed(() => !!slots['details-dialog']);
        const showRestoreDefaultViewButton = computed(() => !isDefaultView());
        const editDialogTitleComputed = computed(() => {
            return isEditing.value ? props.editDialogTitle ?? t('common.dataGrid.editTitle') : props.addDialogTitle ?? t('common.dataGrid.addTitle');
        });
        const { visibleHeaders, headersForList } = useHeadersBuilder({
            currentHeaders,
            showSelect: () => props.showSelect,
            showSingleSelect: () => props.showSingleSelect,
            showExpand: () => props.showExpand,
            preserveColumnWidth: () => props.preserveColumnWidth,
            showActions: () => !!(props.allowEditing || props.allowDeleting || slots.actions || props.additionalActions || props.contextMenuActions),
            actionColumnWidth: () => props.actionColumnWidth,
            includeExportOptions: true,
            keyDependency: () => dataGridKey.value,
        });
        // Selection
        const { itemCannotBeSelected, isSelected, toggleSelectItem, toggleSelectAll, allSelected, someSelected, } = useDataGridSelection({
            items: displayItems,
            selectedItems: () => props.selectedItems,
            itemKey: () => props.itemKey,
            singleSelect: () => props.showSingleSelect,
            itemSelectable: () => props.itemSelectable,
            onUpdateSelectedItems: (items) => emit('update:selectedItems', items),
        });
        // Expansion
        const { isExpanded, toggleExpand, } = useDataGridExpansion({
            itemKey: () => props.itemKey,
            singleExpand: () => props.singleExpand,
        });
        const noDataDescription = computed(() => hasFiltersSet.value ? t('common.dataGrid.noData.filtersExist.description') : t('common.dataGrid.noData.description'));
        const noDataText = computed(() => hasFiltersSet.value ? t('common.dataGrid.noData.filtersExist.text') : t('common.dataGrid.noData.text'));
        // Logic
        /**
         *
         * A new load aborts the running one and discards its result. Dropping the new load instead
         * would leave the grid on a result set the filter bar and URL no longer describe.
         **/
        async function loadItems(loadForExport = false, cancelSignal = undefined) {
            if (!props.getUri)
                return;
            // Exports have their own signal and don't touch grid state, so they skip all of this.
            let isCurrentLoad = () => true;
            if (!loadForExport) {
                // A load that is still running was started for older filters, so its result is useless now.
                if (loadController) {
                    loadController.abort();
                }
                const controller = new AbortController();
                loadController = controller;
                cancelSignal = controller.signal;
                // The next load overwrites loadController. If it still points at ours, no newer load started.
                // We check this because aborting cannot stop a response that is already on its way.
                isCurrentLoad = () => loadController === controller;
            }
            loading.value = true;
            emit('update:isLoading', true);
            try {
                const params = makeGetParameters();
                const queryString = new URLSearchParams();
                // For export shouldn't apply take, but rather download everything
                if (loadForExport) {
                    delete params.take;
                }
                Object.entries(params).forEach(([key, value]) => {
                    if (value !== null && value !== undefined) {
                        queryString.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
                    }
                });
                const url = `${props.getUri}${props.getUri.includes('?') ? '&' : '?'}${queryString.toString()}`;
                const response = await enFetch(url, { signal: cancelSignal });
                if (!isCurrentLoad())
                    return null;
                if (response && response.data) {
                    let data = response.data;
                    if (props.mapping) {
                        props.mapping(data);
                    }
                    await loadLookups(data);
                    data.forEach((item) => loadLookUpDataIntoMappedItem(item));
                    if (loadForExport) {
                        return data;
                    }
                    else {
                        internalItems.value = data;
                        internalTotalItems.value = response.totalCount || response.data.length;
                        appliedOptions = cloneDeep(options.value);
                        appliedFilters = cloneDeep(filters.value);
                        emit('update:items', internalItems.value);
                        emit('update:totalItems', internalTotalItems.value);
                    }
                }
            }
            catch (error) {
                // Aborting is the expected way to supersede or cancel a load, not a failure.
                if (!(error instanceof DOMException && error.name === 'AbortError') && !cancelSignal?.aborted) {
                    console.error('Failed to load data:', error);
                }
            }
            finally {
                if (isCurrentLoad()) {
                    loadController = null;
                    loading.value = false;
                    emit('update:isLoading', false);
                }
            }
            return loadForExport ? null : internalItems.value;
        }
        /**
         * Stops the running load and puts the grid back on the items, options and filters it was showing before.
         */
        async function cancelLoad() {
            loadController?.abort();
            // The pagination holds on to this object, so restore into it instead of replacing it.
            Object.assign(options.value, cloneDeep(appliedOptions));
            emit('update:options', options.value);
            if (!appliedFilters)
                return;
            restoringAppliedState = true;
            try {
                filters.value = cloneDeep(appliedFilters);
                await updateFilters();
                await nextTick();
            }
            finally {
                restoringAppliedState = false;
            }
        }
        async function applyFilter(key, { value, type, ensureVisible = true } = {}) {
            let filter = filters.value?.[key];
            if (key.includes('.')) {
                const [parentKey, childKey] = key.split('.');
                filter = filters.value?.[parentKey]?.[childKey];
            }
            if (!filter)
                return;
            filter.value = value;
            if (type !== undefined) {
                if (Object.prototype.hasOwnProperty.call(filter, 'filterType')) {
                    filter.filterType = type;
                }
            }
            const fb = filterButtons.value?.find(b => b.key === (key.includes('.') ? key.split('.')[0] : key));
            if (fb && ensureVisible) {
                fb.visible = true;
                fb.openOnRender = false;
            }
            await updateFilters();
        }
        function makeGetParameters() {
            if (props.getParameters) {
                let result = { ...props.getParameters };
                result.skip = (options.value.page - 1) * options.value.itemsPerPage;
                result.take = options.value.itemsPerPage;
                result.requireTotalCount = props.requireTotalCount;
                return result;
            }
            const getParams = {
                requireTotalCount: props.requireTotalCount,
                take: options.value.itemsPerPage,
                skip: (options.value.page - 1) * options.value.itemsPerPage,
                filter: filterExpr.value.length > 0 ? JSON.stringify(filterExpr.value) : null,
                sort: makeSortOrderFetchParameter(),
            };
            if (props.additionalGetParams) {
                props.additionalGetParams.forEach(param => {
                    getParams[param.key] = param.value;
                });
            }
            return getParams;
        }
        function makeSortOrderFetchParameter() {
            if (options.value.sortBy && options.value.sortBy.length > 0) {
                return JSON.stringify(options.value.sortBy.map((s) => ({
                    selector: getSortKey(s.key),
                    desc: s.order === ColumnSortDirections.Descending
                })));
            }
            return null;
        }
        function getSortKey(key) {
            if (!props.customSortMap)
                return key;
            // ToDo (PTA) first step to use normal id->name instead of an old one
            const item = props.customSortMap.find(m => m.key === key || m.id === key);
            return item?.value ?? item?.name ?? key;
        }
        // CRUD Operations
        function addItem() {
            // Clone so the dialog edits a fresh item instead of mutating the caller's defaultItem,
            // which would leak the previous entry into the next add.
            currentItem.value = typeof props.defaultItem === VarType.Function ? props.defaultItem?.() : cloneDeep(props.defaultItem) ?? {};
            showEditDialog.value = true;
            isEditing.value = false;
            isAdding.value = true;
            emit('edited-dialog-opened', currentItem.value);
        }
        const addingDialogIsVisible = computed(() => {
            return slots.addDialog && !isEditing.value;
        });
        function editItem(item) {
            currentItem.value = cloneDeep(item);
            showEditDialog.value = true;
            emit('edited-dialog-opened', currentItem.value);
            isEditing.value = true;
        }
        function deleteItem(item) {
            currentItem.value = item;
            showDeleteDialog.value = true;
        }
        async function saveItem() {
            if (isAdding.value) {
                await postItem();
            }
            else {
                await putItem();
            }
        }
        async function postItem() {
            if (!postUrl && !props.customPostItem && props.getUri)
                return;
            try {
                const originalItem = currentItem.value;
                const item = props.createPostItem ? props.createPostItem(currentItem.value) : currentItem.value;
                if (!props.getUri) {
                    if (props.customPostItem)
                        await props.customPostItem(item);
                    emit('item-added', item);
                    closeEditDialog();
                    return;
                }
                const formBody = new FormData();
                formBody.append('values', JSON.stringify(item));
                const response = props.customPostItem
                    ? await props.customPostItem(item)
                    : await enFetch(buildUri(postUrl), { method: RequestMethods.Post, body: formBody, noResponseBody: true });
                if (response && response.ok !== false) {
                    emit('item-added', item);
                    closeEditDialog();
                    snackbar.value?.pushMessage(t('common.snackbar.saved'));
                    await loadItems();
                }
                else {
                    editDialogErrorMessage.value = parseResponseException(response?.message || await response?.text?.());
                    showEditDialog.value = true;
                    currentItem.value = originalItem;
                    await nextTick();
                    top.value?.validationErrorRef?.scrollToMe();
                }
            }
            catch (error) {
                editDialogErrorMessage.value = t('common.dataGrid.postError');
            }
        }
        async function putItem() {
            if (!putUrl && !props.customPutItem && props.getUri)
                return;
            try {
                const item = props.createPutItem ? props.createPutItem(currentItem.value) : currentItem.value;
                if (!props.getUri) {
                    if (props.customPutItem)
                        await props.customPutItem(item);
                    emit('item-updated', item);
                    closeEditDialog();
                    return;
                }
                const formBody = new FormData();
                formBody.append('key', currentItem.value[props.itemKey]);
                formBody.append('values', JSON.stringify(item));
                const response = props.customPutItem
                    ? await props.customPutItem(item)
                    : await enFetch(buildUri(putUrl), { method: RequestMethods.Put, body: formBody });
                // Reads the body of an error response, so it must not be read again below
                await handleServerResponse(response, t('common.dataGrid.putError'));
                if (response && response.ok !== false) {
                    showEditDialog.value = false;
                    emit('item-updated', item);
                }
                else if (response?.message) {
                    editDialogErrorMessage.value = parseResponseException(response.message);
                }
            }
            catch (error) {
                editDialogErrorMessage.value = t('common.dataGrid.putError');
            }
        }
        /**
         * If the given uri is of type function executes the function to generate uri.
         * Otherwise, the uri is returned.
         * If uri is a function it is called with the component as first parameter and currentItem as second parameter.
         * @param {String|Function} uri
         * @returns {String}
         */
        function buildUri(uri) {
            if (typeof uri == VarType.Function) {
                return uri.call(this, currentItem.value);
            }
            return uri;
        }
        async function deleteItemConfirm() {
            if (!deleteUrl && !props.customDeleteItem && props.getUri)
                return;
            try {
                if (!props.getUri) {
                    if (props.customDeleteItem)
                        await props.customDeleteItem(currentItem.value);
                    emit('item-deleted', currentItem.value);
                    closeDeleteDialog();
                    return;
                }
                const formBody = new FormData();
                formBody.append('key', currentItem.value[props.itemKey]);
                const response = props.customDeleteItem
                    ? await props.customDeleteItem(currentItem.value)
                    : await enFetch(buildUri(deleteUrl), {
                        method: RequestMethods.Delete,
                        body: formBody,
                        noResponseBody: true
                    });
                await handleServerResponse(response, t('common.dataGrid.putError'));
                if (response && response.ok !== false) {
                    showDeleteDialog.value = false;
                    emit('item-deleted', currentItem.value);
                }
                else {
                    snackbar.value?.pushMessage(parseResponseException(response?.message || await response?.text?.()));
                }
            }
            catch (error) {
                console.error('Delete failed:', error);
            }
        }
        async function handleServerResponse(response, defaultErrorMessage, isDeleting = false) {
            // If response was not successful
            if (response !== null && response !== undefined && !response.ok) {
                const serverErrorMessage = await getServerErrorMessage(response);
                const errorMessageText = isNullOrEmpty(serverErrorMessage)
                    ? defaultErrorMessage
                    : parseResponseException(serverErrorMessage);
                if (isDeleting) {
                    snackbarType.value = SnackbarType.ERROR;
                    snackbar.value?.pushMessage(errorMessageText);
                }
                else {
                    snackbarType.value = SnackbarType.SUCCESS;
                    editDialogErrorMessage.value = errorMessageText;
                    await scrollToEditDialogError();
                }
                return;
            }
            // Close necessary window
            if (isDeleting) {
                closeDeleteDialog();
                snackbarType.value = SnackbarType.SUCCESS;
                snackbar.value?.pushMessage(t('common.snackbar.deleted'));
            }
            else {
                //If it was not delete, it was edit/add. In either way it was successful
                closeEditDialog();
                snackbarType.value = SnackbarType.SUCCESS;
                snackbar.value?.pushMessage(t('common.snackbar.saved'));
            }
            await loadItems();
        }
        function closeDeleteDialog() {
            showDeleteDialog.value = false;
            resetCurrentItem();
            /**
             * Triggers when the delete dialog was closed.
             */
            emit('delete-dialog-closed');
        }
        function closeEditDialog() {
            showEditDialog.value = false;
            isEditing.value = false;
            isAdding.value = false;
            editDialogErrorMessage.value = null;
            resetValidation();
            resetCurrentItem();
            /**
             * Triggers when the editing dialog is closed
             */
            emit('edit-dialog-closed');
        }
        function resetValidation() {
            return top.value?.editDialogForm?.resetValidation();
        }
        function resetCurrentItem() {
            if (typeof props.defaultItem === VarType.Function) {
                currentItem.value = props.defaultItem();
            }
            else if (typeof props.defaultItem === VarType.Object) {
                currentItem.value = Object.assign({}, props.defaultItem);
            }
            else {
                currentItem.value = {};
            }
            emit('update:modelValue', currentItem.value);
        }
        async function getServerErrorMessage(response) {
            if (typeof response?.text === 'function') {
                return await response.text();
            }
            if (typeof response === 'string') {
                return response;
            }
            return null;
        }
        async function scrollToEditDialogError() {
            await nextTick();
            const element = document.getElementById('edit-dialog-error-message');
            if (element)
                element.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'nearest' });
        }
        function copyItem(item) {
            currentItem.value = cloneDeep(item);
            showEditDialog.value = true;
            isEditing.value = false;
            isAdding.value = true;
            emit('edited-dialog-opened', currentItem.value);
        }
        function startDeleteItem(item) {
            deleteItem(item);
        }
        function showContextMenuViaIcon(item) {
            // Usually handled by the menu component itself
            contextMenuItem.value = item;
        }
        async function resetIndividualFilter(filterKey, { hideButton = true } = {}) {
            if (hideButton) {
                const filterButton = filterButtons.value?.find(button => button.key === filterKey);
                if (filterButton)
                    filterButton.visible = false;
            }
            await resetFilters(filterKey);
        }
        function exportAllData() {
            showExportDialog.value = true;
        }
        function resetExport() {
            showExportDialog.value = false;
        }
        /**
         * The datagrid without columns shows nothing at all, so the last column on display stays.
         */
        function isLastVisibleColumn(column) {
            return !column.isHidden
                && currentHeaders.value.filter(header => header.canBeToggled && !header.isHidden).length === 1;
        }
        /**
         * Hides the column, or shows it again.
         */
        function toggleVisibilityColumn(key) {
            const header = currentHeaders.value.find(header => header.value === key);
            if (header) {
                if (isLastVisibleColumn(header))
                    return;
                header.isHidden = !header.isHidden;
                emit('column-visibility-toggle', header);
            }
            saveColumnState();
        }
        function showAllColumns() {
            currentHeaders.value.forEach(header => {
                if (header.canBeToggled) {
                    header.isHidden = false;
                }
            });
            emit('column-visibility-toggle-all');
            saveColumnState();
            dataGridKey.value++;
        }
        /**
         * Puts the data grid back into the view its module defines and drops the saved one.
         */
        function restoreDefaultView() {
            restoreDefaultColumnState();
            emit('column-visibility-toggle-all');
            dataGridKey.value++;
            options.value.page = 1;
            loadItems();
        }
        function updateSortBy(newSortArray) {
            options.value.sortBy = newSortArray;
            options.value.page = 1;
            saveColumnState();
            loadItems();
        }
        function updateOptions(key, value) {
            if (key === 'sortBy' && Array.isArray(value)) {
                updateSortBy(value);
                return;
            }
            options.value[key] = value;
            emit('update:options', options.value);
            if (props.getUri)
                loadItems();
        }
        const dialogSaveButtonText = computed(() => {
            if (props.editDialogOptions?.confirmButtonText)
                return props.editDialogOptions?.confirmButtonText;
            return isEditing.value ? t('common.dataGrid.edit') : t('common.dataGrid.create');
        });
        function parseFilterForPresetData(value) {
            // If we do not save state in url, we shouldn't look there for create filters as well
            if (!props.saveStateInUrl)
                return { value: undefined, filterType: undefined };
            // Only parse JSON when the value is an object-like string. This preserves primitives like "22.00".
            if (value === null ||
                value === undefined ||
                value === '' ||
                (typeof value === 'string' && value.toLowerCase() === 'null')) {
                return { value: undefined, filterType: null };
            }
            let hasFilterType = false;
            let filter = value;
            if (typeof value === 'string') {
                const trimmed = value.trim();
                // required for handle array from the url
                filter = tryParseJson(trimmed);
                if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
                    try {
                        filter = JSON.parse(trimmed);
                        hasFilterType = true;
                    }
                    catch {
                        // Not a valid JSON object; keep as string
                    }
                }
            }
            if (hasFilterType) {
                return { value: filter?.value ?? filter, filterType: filter?.type };
            }
            else {
                // We do not use FilterType.Equal as default. Should be handled by creation filter
                return { value: filter, filterType: null };
            }
        }
        /**
         * Try to parse input string as a valid Array. If is invalid, return the same value
         * @param unparsedValue
         * @returns {*}
         */
        function tryParseJson(unparsedValue) {
            try {
                return JSON.parse(unparsedValue);
            }
            catch (_) {
                return unparsedValue;
            }
        }
        function createFilters() {
            const currentFilters = cloneDeep(filters.value);
            const currentFilterButtons = cloneDeep(filterButtons.value);
            filters.value = {};
            filterButtons.value = [];
            const queryString = window.location.search;
            props.headers.forEach(header => {
                const localOptions = header.filterOptions;
                const filterButton = {};
                if (localOptions?.isHidden && localOptions?.isDynamic !== true) {
                    return;
                }
                if (localOptions) {
                    const key = localOptions.key ?? header.value;
                    const keyForId = camelCaseToKebabCase(key).replace('.', '');
                    let filterButtonIsVisible = false;
                    if (currentFilters[key]) {
                        // If headers prop is updated the filters have to be created again. This is necessary because filters can be removed and/or added this way.
                        // In case the filter is still present in the header's filter config it should not be generated again and therefore the old one is kept. This preserves the filter's value.
                        // Note: the filter's configuration won't be updated if it is kept. If you need this to happen consider creating a new filter instead.
                        filters.value[key] = currentFilters[key];
                        filterButtonIsVisible = currentFilterButtons.find((fb) => fb.key === key)?.visible;
                    }
                    else {
                        if (localOptions.filterButtonType === FilterButtonType.Address) {
                            filterButton.properties = localOptions.properties;
                            filters.value[key] = {};
                            new URLSearchParams(queryString).forEach((value, keyParam) => {
                                // Check if the parameter starts with any prefix, i.e. "Address-"
                                if (keyParam.startsWith(`${key}-`)) {
                                    const nestedKey = keyParam.substring(keyParam.indexOf('-') + 1);
                                    const potentialValue = parseFilterForPresetData(value);
                                    filters.value[key][nestedKey] = { value: potentialValue.value, filterType: potentialValue.filterType };
                                    if (filters.value[key][nestedKey].value !== filters.value[key][nestedKey].resetValue) {
                                        filterButtonIsVisible = true;
                                    }
                                }
                            });
                        }
                        else if (localOptions.filterButtonType === FilterButtonType.InputMultiple) {
                            const localFilters = localOptions.filters;
                            for (const filterKey in localFilters) {
                                const potentialValue = parseFilterForPresetData(new URLSearchParams(queryString).get(`${key}-${filterKey}`));
                                localFilters[filterKey].value = potentialValue.value;
                                localFilters[filterKey].filterType = potentialValue.filterType === null
                                    ? (localFilters[filterKey].resetFilterType ?? localFilters[filterKey].filterType ?? FilterType.Equal)
                                    : potentialValue.filterType;
                                const hasValue = localFilters[filterKey].value != null;
                                const hasResetValue = localFilters[filterKey].resetValue != null;
                                if (hasValue !== hasResetValue || (hasValue && localFilters[filterKey].value !== localFilters[filterKey].resetValue)) {
                                    filterButtonIsVisible = true;
                                }
                            }
                            filters.value[key] = localFilters;
                        }
                        else {
                            // Obtain values from url. Only works for simple filters. For addresses and multiple should be handled manually
                            const potentialValue = parseFilterForPresetData(new URLSearchParams(queryString).get(key));
                            const value = localOptions.value ?? potentialValue.value ?? localOptions.resetValue;
                            // If is url's filter type is null, we should check resetFilterType or set default as FilterType.Equal
                            const filterType = potentialValue.filterType === null ? (localOptions.filterSearchType ?? FilterType.Equal) : potentialValue.filterType;
                            filters.value[key] = new Filter({
                                value: value,
                                resetValue: localOptions.resetValue ?? undefined,
                                dataField: localOptions.dataField ?? header.value,
                                combine: localOptions.combine,
                                filterProperty: localOptions.filterProperty,
                                createFilterValue: localOptions.createFilterValue,
                                createFilter: localOptions.createFilter,
                                isGetParam: localOptions.isGetParam,
                                allowNull: localOptions.allowNull,
                                allowedDates: localOptions.allowedDates,
                                filterType: filterType,
                                resetFilterType: localOptions.resetFilterType !== undefined ? localOptions.resetFilterType : undefined,
                            });
                            if (filters.value[key].value !== filters.value[key].resetValue) {
                                filterButtonIsVisible = true;
                            }
                        }
                    }
                    filterButton.id = keyForId;
                    filterButton.key = key;
                    filterButton.type = localOptions.filterButtonType;
                    filterButton.label = localOptions.label ?? header.title;
                    filterButton.hint = localOptions.hint;
                    filterButton.hasFilterTypeSelection = localOptions.hasFilterTypeSelection;
                    filterButton.selectedValue = localOptions.value;
                    filterButton.onLoadingFromUrl = localOptions.onLoadingFromUrl;
                    filterButton.visible = filterButtonIsVisible;
                    filterButton.openOnRender = false;
                    switch (localOptions.filterButtonType) {
                        case FilterButtonType.Input:
                            break;
                        case FilterButtonType.Select:
                            filterButton.options = localOptions.selectItems;
                            filterButton.selectionVariant = localOptions.selectionVariant;
                            break;
                        case FilterButtonType.Date:
                        case FilterButtonType.DateMonth:
                            filterButton.allowedDates = filters.value[key].allowedDates;
                            break;
                        case FilterButtonType.Address:
                            filterButton.isAutocomplete = localOptions.isAutocomplete;
                            filterButton.apiUri = localOptions.isAutocomplete ? localOptions.apiUri ?? props.getUri : localOptions.apiUri;
                            filterButton.dataFields = localOptions.dataFields;
                            filterButton.properties = localOptions.properties;
                            filterButton.displaySeparator = localOptions.displaySeparator;
                            filterButton.combineNestedFilters = localOptions.combineNestedFilters;
                            filterButton.allowLike = localOptions.allowLike;
                            filterButton.fullValueAutocomplete = localOptions.fullValueAutocomplete;
                            filterButton.hasPosition = localOptions.hasPosition;
                            filterButton.hasConsumptionPointType = localOptions.hasConsumptionPointType;
                            filterButton.hasHouseNumber = localOptions.hasHouseNumber;
                            filterButton.hasLabel = localOptions.hasLabel;
                            break;
                        case FilterButtonType.Autocomplete: {
                            const useGroupDefault = (!localOptions.itemValue && !localOptions.group) ?? false;
                            filterButton.apiUri = localOptions.items ? undefined : (localOptions.apiUri ?? props.getUri);
                            filterButton.items = localOptions.items;
                            filterButton.itemValue = localOptions.itemValue ?? header.value;
                            filterButton.group = useGroupDefault && !localOptions.group ? header.value : localOptions.group;
                            filterButton.hideNullValues = localOptions.hideNullValues;
                            filterButton.searchExpression = localOptions.createSearchExpressionFromActiveFilters ? (searchTerm) => searchExpressionFromActiveFilters(searchTerm, key) : localOptions.searchExpression ?? header.value;
                            filterButton.searchValue = localOptions.searchValue;
                            filterButton.selectExpression = localOptions.selectExpression;
                            filterButton.useContains = localOptions.useContains;
                            filterButton.displayExpression = localOptions.displayExpression ?? header.value;
                            filterButton.hint = localOptions.hint ?? t('common.dataGrid.autocompleteFilterHint', { label: filterButton.label });
                            filterButton.noFilterText = localOptions.noFilterText;
                            // these default values (itemValue, group, searchExpression, and displayExpression are all equal to the header value)
                            // are appropriate for autocompletes which use the same api address as the table
                            // (where we need to group to get enough distinct values for enums and similar columns)
                            // however, grouping on large tables may run into performance issues
                            break;
                        }
                    }
                    if (filterButtonIsVisible && localOptions?.isHidden || !localOptions?.isHidden) {
                        filterButtons.value.push(filterButton);
                    }
                }
            });
            if (props.additionalHiddenFilters) {
                filters.value = {
                    ...filters.value,
                    ...props.additionalHiddenFilters,
                };
            }
            loadColumnState();
        }
        function searchExpressionFromActiveFilters(searchTerm, key) {
            const filter = createFilterExpression(filters.value, [key]);
            // In case when no filters applied, [[]] shouldn't pass as current filter
            if (filter[0]?.length === 0) {
                filter.length = 0;
            }
            if (searchTerm) {
                const dataFields = filters.value[key].dataField;
                if (typeof dataFields === VarType.String) {
                    addTermToFilter(filter, [filters.value[key].dataField, FilterType.Contains, searchTerm]);
                }
                else {
                    // "dataFields" is an array
                    const tempFilter = [];
                    dataFields.forEach((dataField) => {
                        addTermToFilter(tempFilter, [dataField, FilterType.Contains, searchTerm], LogicalOperator.Or);
                    });
                    addTermToFilter(filter, tempFilter);
                }
            }
            return filter;
        }
        function toggleFilterVisibility(column) {
            const filterButton = filterButtons.value.find(button => button.key === column.value);
            if (filterButton) {
                filterButton.visible = !filterButton.visible;
                if (!filterButton.visible) {
                    resetIndividualFilter(filterButton.key);
                }
                else {
                    if (filterButton) {
                        filterButton.openOnRender = true;
                    }
                }
            }
        }
        function filtersChanged() {
            emit('filters-changed', filters.value);
        }
        function hoverCell(header) {
            hoveredColumnKey.value = header.value;
        }
        function toggleDialog(newVal) {
            if (!newVal) {
                closeEditDialog();
            }
            else {
                editDialogErrorMessage.value = null;
                emit('edited-dialog-opened', currentItem.value);
            }
        }
        onBeforeMount(async () => {
            createFilters();
            if (Object.keys(filters.value).length > 0) {
                await loadFiltersAndUpdate();
            }
            else {
                // TODO(VHA) Rename, when all modules use integrated filter logic for AsyncDataGrid
                noLoading.value = false;
                await loadItems();
            }
        });
        watch(() => props.items, (newItems) => {
            if (!props.getUri) {
                internalItems.value = newItems || [];
            }
        }, { immediate: true });
        watch(currentItem, (newItem) => {
            emit('update:modelValue', newItem);
        });
        watch(filterExpr, () => {
            if (restoringAppliedState)
                return;
            options.value.page = 1;
            loadItems();
        }, { deep: true });
        watch(filters, () => {
            // Re-execute composable with the updated filters
            ({
                updateFilters,
                resetFilters,
                loadFiltersAndUpdate,
            } = useFilterManagement(filters, filterExpr, noLoading, { toUrlParams: columnStateUrlParams }, filtersChanged, router, route, props.saveStateInUrl));
        }, { deep: true });
        watch(() => props.headers, () => {
            dataGridKey.value++;
            currentHeaders.value = props.headers;
            reloadColumnState();
        });
        function validate() {
            return top?.value?.editDialogForm?.validate?.();
        }
        function getRealItemFromTableItem(tableItem) {
            return (props.items ?? displayItems)?.find?.(item => item?.Id === tableItem?.Id);
        }
        function getFilterByKey(key) {
            return filters.value?.[key];
        }
        function removeColumn(key) {
            const header = visibleHeaders.value.find(header => header.value === key);
            if (!header)
                return;
            if (isLastVisibleColumn(header))
                return;
            header.isHidden = !header.isHidden;
            emit('column-visibility-toggle', header);
            dataGridKey.value++;
            saveColumnState();
        }
        /**
         * Applies lookup properties to the given item.
         * @param {Object} item
         */
        function loadLookUpDataIntoMappedItem(item) {
            if (!props.lookups)
                return;
            for (const property in item) {
                const correspondingLookup = props.lookups.find(l => l.property === property);
                if (correspondingLookup) {
                    if (typeof correspondingLookup.referenceIdProperty === VarType.Function) {
                        item[property] = lookupItems[property]?.find((li) => li.Id === correspondingLookup.referenceIdProperty(item));
                    }
                    else {
                        const referenceIdProperty = correspondingLookup.referenceIdProperty ?? `${property}Id`;
                        item[property] = lookupItems[property]?.find((li) => li.Id === item[referenceIdProperty]);
                    }
                }
            }
        }
        /**
         * Loads configured lookups.
         */
        async function loadLookups(allLoadedItems) {
            if (!props.lookups || props.lookups.length === 0)
                return;
            const loadPromises = [];
            for (const lookup of props.lookups) {
                let lookupDownloadedItems = [];
                // Obtain data either from param or from items
                const length = allLoadedItems ? allLoadedItems.length : displayItems.value.length;
                //max length of query can't exceed 2096 symbols. 25 is optimal number, because Id may contain GUID
                const take = lookup.buildQueryWithBody ? length : 25;
                // skip: index lookup
                // total: number of loadings
                // done: number of downloaded lookups
                let total = 0, done = 0;
                const numberOfChunks = length < take ? 1 : length / take;
                for (let i = 0; i < numberOfChunks; i++) {
                    try {
                        if (lookup.buildQuery) {
                            let localTotal = 0, localDone = 0;
                            let urls = [];
                            allLoadedItems.forEach(item => {
                                const input = lookup.buildQuery(item);
                                if (input) {
                                    if (!urls.includes(input)) {
                                        loadPromises.push(enFetch(input, { noResponseBody: true }).then((res) => res.json())
                                            .then(data => {
                                            localDone++;
                                            lookupDownloadedItems = [...lookupDownloadedItems, ...data.data];
                                            if (localDone === localTotal) {
                                                lookupItems[lookup.property] = lookupDownloadedItems;
                                            }
                                        }));
                                        urls.push(input);
                                        localTotal++;
                                    }
                                }
                            });
                        }
                        else if (lookup.buildQueryWithBody) {
                            let localTotal = 0, localDone = 0;
                            let urls = [];
                            const inputs = lookup.buildQueryWithBody(allLoadedItems);
                            if (inputs) {
                                inputs.forEach((input) => {
                                    loadPromises.push(enFetch(input.url, {
                                        method: input.method, body: input.body, noResponseBody: true
                                    }).then((res) => res.json())
                                        .then(data => {
                                        localDone++;
                                        lookupDownloadedItems = [...lookupDownloadedItems, ...data];
                                        if (localDone === localTotal) {
                                            lookupItems[lookup.property] = lookupDownloadedItems;
                                        }
                                    }));
                                    urls.push(input);
                                    localTotal++;
                                });
                            }
                        }
                        else {
                            const params = getLookupQueryParameters(lookup, i * take, take);
                            const input = typeof lookup.getUri === VarType.Function ? lookup.getUri() : lookup.getUri + '?' + params;
                            loadPromises.push(enFetch(input).then(res => res.json())
                                .then(data => {
                                done++;
                                lookupDownloadedItems = [...lookupDownloadedItems, ...data.data];
                                if (done === total) {
                                    lookupItems[lookup.property] = lookupDownloadedItems;
                                }
                            }));
                        }
                        total++;
                    }
                    catch {
                        //if filter is null, an error will be thrown, but it is handled here
                    }
                }
            }
            await Promise.all(loadPromises);
        }
        /**
         * Creates lookup query parameters in our filter style
         * @param {Object} lookupConfig - the lookup configuration
         * @param {Number} skip - index from which element should load lookups
         * @param {Number} take - how many lookups should be downloaded
         * @returns {Object}
         */
        function getLookupQueryParameters(lookupConfig, skip, take) {
            // restrict to ids of displayItems
            const ids = [...new Set(displayItems.value.slice(skip, skip + take).map(i => i[lookupConfig.referenceIdProperty]))];
            if (ids.length === 1 && ids[0] === null) {
                throw 'empty filter';
            }
            const idFilters = ids.map(id => ['Id', '=', id]);
            const filter = [];
            // join id filters with 'or'
            let isFirstFilter = true;
            for (const idFilter of idFilters) {
                if (isFirstFilter) {
                    isFirstFilter = false;
                }
                else {
                    filter.push('or');
                }
                filter.push(idFilter);
            }
            return new URLSearchParams({
                filter: JSON.stringify(filter),
            });
        }
        function sortTheHeadersAndUpdateTheKey(evt) {
            // The header list prepends the selection and expansion columns, so the dragged indexes are shifted by them.
            let offset = 0;
            if (props.showSelect || props.showSingleSelect)
                offset++;
            if (props.showExpand)
                offset++;
            const oldIndex = evt.oldIndex - offset;
            const newIndex = evt.newIndex - offset;
            const orderedDataFields = visibleHeaders.value.map(header => header.value);
            if (oldIndex === newIndex || !orderedDataFields[oldIndex] || !orderedDataFields[newIndex])
                return;
            orderedDataFields.splice(newIndex, 0, orderedDataFields.splice(oldIndex, 1)[0]);
            reorderColumns(orderedDataFields);
            // The drag moved the header elements themselves, so the table is rendered again to match the new order.
            dataGridKey.value++;
        }
        /**
         * Displays the named columns in the given order and saves the new view.
         * @param orderedDataFields Data fields of the reordered columns, in their new order.
         */
        function reorderColumns(orderedDataFields) {
            applyColumnOrder(orderedDataFields);
            saveColumnState();
        }
        function handleRowClick(item, event) {
            emit('row-clicked', item);
            // Selecting, expanding and the row actions have their own click handling and must not open the dialog.
            const isControlClick = event?.target?.closest?.('.sticky-checkbox-cell, .expand-col, .item-actions');
            if (hasDetailsDialog.value && !isControlClick)
                detailsNavigation.openItem(item);
        }
        function onContextMenu(event, _) {
            event.preventDefault();
            // at the moment we do not want to handle it
            return;
        }
        function getFilterValueByKey(key) {
            const filter = filters.value?.[key];
            if (!filter)
                return undefined;
            return Array.isArray(filter.value) ? filter.value[0] : filter.value;
        }
        function buildIdForExpandButton(item) {
            return buildIdForItem(`${props.staticId}-expand-menu-action-button`, props.itemKey, item);
        }
        function buildIdForSelectButton(item) {
            return buildIdForItem(`${props.staticId}-checkbox-menu-action-button`, props.itemKey, item);
        }
        provide('batchProcessItemCount', batchProcessItemCount);
        provide('batchProcessTitle', batchProcessTitle);
        provide('reload', {});
        __expose({
            columnState,
            // The columns menu of a datagrid whose own filter bar is hidden, e.g. because the module brings
            // its own, is rendered from these.
            columns: currentHeaders,
            showAllColumns,
            toggleVisibilityColumn,
            reorderColumns,
            showRestoreDefaultViewButton,
            restoreDefaultView,
            applyFilter,
            validate,
            resetValidation,
            loadItems,
            items: displayItems,
            totalItems: displayTotalItems,
            getRealItemFromTableItem,
            resetIndividualFilter,
            getFilterByKey,
            getFilterValueByKey,
            options,
        });
        provide('reload', { loadItems });
        const __returned__ = { t, slots, parseResponseException, props, emit, modelValue, snackbar, top, pagination, tableWrapper, stickyCheckboxWidth, internalItems, dataGridKey, internalTotalItems, lookupItems, showExportDialog, loading, get loadController() { return loadController; }, set loadController(v) { loadController = v; }, contextMenuItem, snackbarType, currentItem, hoveredColumnKey, batchProcessItemCount, batchProcessTitle, showEditDialog, showDeleteDialog, editDialogErrorMessage, isEditing, isAdding, postUrl, putUrl, deleteUrl, filterExpr, noLoading, filterButtons, filters, router, route, options, calculateSkeletonWidth, currentHeaders, columnState, isDefaultView, applyColumnOrder, loadColumnState, reloadColumnState, saveColumnState, restoreDefaultColumnState, columnStateUrlParams, get updateFilters() { return updateFilters; }, set updateFilters(v) { updateFilters = v; }, get resetFilters() { return resetFilters; }, set resetFilters(v) { resetFilters = v; }, get createFilterExpression() { return createFilterExpression; }, set createFilterExpression(v) { createFilterExpression = v; }, get loadFiltersAndUpdate() { return loadFiltersAndUpdate; }, set loadFiltersAndUpdate(v) { loadFiltersAndUpdate = v; }, buildIdForItem, get appliedOptions() { return appliedOptions; }, set appliedOptions(v) { appliedOptions = v; }, get appliedFilters() { return appliedFilters; }, set appliedFilters(v) { appliedFilters = v; }, get restoringAppliedState() { return restoringAppliedState; }, set restoringAppliedState(v) { restoringAppliedState = v; }, displayItems, hasFiltersSet, bodyHeight, displayTotalItems, detailsNavigation, hasDetailsDialog, showRestoreDefaultViewButton, editDialogTitleComputed, visibleHeaders, headersForList, itemCannotBeSelected, isSelected, toggleSelectItem, toggleSelectAll, allSelected, someSelected, isExpanded, toggleExpand, noDataDescription, noDataText, loadItems, cancelLoad, applyFilter, makeGetParameters, makeSortOrderFetchParameter, getSortKey, addItem, addingDialogIsVisible, editItem, deleteItem, saveItem, postItem, putItem, buildUri, deleteItemConfirm, handleServerResponse, closeDeleteDialog, closeEditDialog, resetValidation, resetCurrentItem, getServerErrorMessage, scrollToEditDialogError, copyItem, startDeleteItem, showContextMenuViaIcon, resetIndividualFilter, exportAllData, resetExport, isLastVisibleColumn, toggleVisibilityColumn, showAllColumns, restoreDefaultView, updateSortBy, updateOptions, dialogSaveButtonText, parseFilterForPresetData, tryParseJson, createFilters, searchExpressionFromActiveFilters, toggleFilterVisibility, filtersChanged, hoverCell, toggleDialog, validate, getRealItemFromTableItem, getFilterByKey, removeColumn, loadLookUpDataIntoMappedItem, loadLookups, getLookupQueryParameters, sortTheHeadersAndUpdateTheKey, reorderColumns, handleRowClick, onContextMenu, getFilterValueByKey, buildIdForExpandButton, buildIdForSelectButton, get Table() { return Table; }, get TableBody() { return TableBody; }, get TableCell() { return TableCell; }, get TableHead() { return TableHead; }, get TableHeader() { return TableHeader; }, get TableRow() { return TableRow; }, EnDatagridTopPanel, EnDatagridHeaderList, EnDatagridPagination, EnDatagridContextMenu, EnExportAllDataDialog, EnCheckbox, EnSnackbar, get EnButton() { return EnButton; }, EnSearchIcon, get EnSkeleton() { return EnSkeleton; }, EnRadioGroupItem, get resolveNestedValue() { return resolveNestedValue; }, get ChevronDown() { return ChevronDown; }, get ChevronUp() { return ChevronUp; }, get LoaderCircle() { return LoaderCircle; } };
        Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true });
        return __returned__;
    }
});
